
import * as Yup from 'yup';
import { enqueueSnackbar } from 'notistack';
import { useMemo, useEffect, useCallback } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';

import Stack from '@mui/material/Stack';
import { LoadingButton } from '@mui/lab';
import Grid from '@mui/material/Unstable_Grid2';
import { Card, MenuItem, TextField, CardContent, InputAdornment } from '@mui/material';

import { useCustomFields } from 'src/hooks/use-custom-fields';

import { TITLE_OPTIONS } from 'src/utils/title-options';

import { useTranslate } from 'src/locales';
import { LinkTypeMeta } from 'src/services/common.types';
import { LinkType } from 'src/services/common/common.types';
import { CustomFieldEntityType } from 'src/services/custom-fields/custom-field.types';
import { ContactType, ContactSource, ContactProfileLink } from 'src/services/contacts/contacts.types';
import { useGetCompanyOrgChartQuery } from 'src/services/connections/connections.service';
import { useGetContactByIdQuery, useUpdateContactMutation, useGetContactCompanyLinksQuery } from 'src/services/contacts/contacts.service';

import Iconify from 'src/components/iconify';
import ListSkeleton from 'src/components/list/list-skeleton';
import FormProvider from 'src/components/hook-form/form-provider';
import { RHFSelect, RHFTextField, RHFMapsAutocomplete } from 'src/components/hook-form';

import { ReportsToField } from '../../companies/detail/sections/org-chart/reports-to-field';
import { findNode, buildOrgForest, collectDescendantIds } from '../../companies/detail/sections/org-chart/build-org-tree';

type Props = {
  contactId: string;
};


export const LinkTypeConfig = [
  {
    type: LinkType.GITHUB,
    label: LinkTypeMeta[LinkType.GITHUB].label,
    icon: LinkTypeMeta[LinkType.GITHUB].icon,
  },
  {
    type: LinkType.LINKEDIN,
    label: LinkTypeMeta[LinkType.LINKEDIN].label,
    icon: LinkTypeMeta[LinkType.LINKEDIN].icon,
  },
  {
    type: LinkType.X,
    label: LinkTypeMeta[LinkType.X].label,
    icon: LinkTypeMeta[LinkType.X].icon,
  },
  {
    type: LinkType.FACEBOOK,
    label: LinkTypeMeta[LinkType.FACEBOOK].label,
    icon: LinkTypeMeta[LinkType.FACEBOOK].icon,
  }
]

export const ContactProfileSection = ({ contactId }: Props) => {

  const { t } = useTranslate();

  const customFields = useCustomFields({
    entityType: CustomFieldEntityType.CONTACT,
    entityId: contactId
  });

  // change to getCompanyByIdQuery
  const { data: contact, isFetching: isLoadingContact } = useGetContactByIdQuery(contactId, {
    skip: !contactId,
  });

  const { data: contactCompanyLinks } = useGetContactCompanyLinksQuery({
    contact_id: contactId,
    params: { page: 1, per_page: 1 },
  }, {
    skip: !contactId,
  });

  const linkedCompanyName = contactCompanyLinks?.results?.[0]?.company_profile?.company_name ?? contact?.company_name ?? '';

  // Org tree: scope the "Reports to" picker to the linked company's contacts (when there is one)
  // and forbid picking this contact's own descendants (client-side cycle guard).
  const linkedCompanyId = contactCompanyLinks?.results?.[0]?.company_profile?.id;
  const { data: companyContacts } = useGetCompanyOrgChartQuery(
    { profile_id: linkedCompanyId as string },
    { skip: !linkedCompanyId }
  );
  const managerExcludeIds = useMemo(() => {
    if (!companyContacts) return [] as string[];
    const forest = buildOrgForest(companyContacts);
    const selfNode = findNode(forest, contactId);
    return selfNode ? Array.from(collectDescendantIds(selfNode)) : [];
  }, [companyContacts, contactId]);

  const [updateContact, { isLoading: isUpdatingContact }] = useUpdateContactMutation();

  const UpdateContactSchema = Yup.object().shape({
    title: Yup.string().nullable(),
    position_title: Yup.string().nullable(),
    first_name: Yup.string().required(t('validation.required')),
    last_name: Yup.string().required(t('validation.required')),
    email: Yup.string().email(t('validation.email_format')).nullable().optional(),
    phone: Yup.string().nullable(),
    location: Yup.string().nullable(),
    contact_type: Yup.mixed<ContactType>().required(t('validation.required')),
    special_information: Yup.string().nullable(),
    reports_to_contact_id: Yup.string().nullable(),
    links: Yup.array().of(
      Yup.object().shape({
        type: Yup.string().required(),
        url: Yup.string()
          .url(t('validation.url'))
          .nullable(),
      })
    ),
  });

  const methods = useForm({
    resolver: yupResolver(UpdateContactSchema),
    defaultValues: {
      title: '',
      position_title: '',
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
      location: '',
      contact_type: ContactType.CANDIDATE,
      special_information: '',
      reports_to_contact_id: null as string | null,
      links: LinkTypeConfig.map(meta => ({
        type: meta.type,
        url: '',
      })),
    },
  });

  const {
    handleSubmit,
    setValue,
    control,
    watch,
    reset,
    formState: { isValid, isSubmitting, errors },
  } = methods;

  const links = watch('links') as ContactProfileLink[];

  useEffect(() => {
    if (contact) {

      const initialLinks = LinkTypeConfig.map(typeMeta => {
        const existingLink = contact.links?.find(link => link.type === typeMeta.type);

        return {
          type: typeMeta.type as LinkType,
          url: existingLink?.url || '',
        };
      });

      reset({
        title: contact.title ?? '',
        position_title: contact.position_title ?? '',
        first_name: contact.first_name,
        last_name: contact.last_name,
        email: contact.email,
        phone: contact.phone,
        location: contact.location ?? '',
        contact_type: contact.contact_type,
        special_information: contact.special_information,
        reports_to_contact_id: contact.reports_to_contact_id ?? null,
        links: initialLinks,
      });
    }
  }, [contact, linkedCompanyName, reset]);

  const handleUpdateContact = handleSubmit(async (data) => {

    try {
      // Validate custom fields first
      const customFieldValidation = customFields.validate();
      if (!customFieldValidation.isValid) {
        return;
      }

      await updateContact({
        contact_id: contactId,
        title: data.title || undefined,
        position_title: data.position_title || undefined,
        first_name: data.first_name,
        last_name: data.last_name,
        email: data.email ?? undefined,
        phone: data.phone ?? undefined,
        location: data.location || undefined,
        reports_to_contact_id: data.reports_to_contact_id ?? null,
        company_profile_id: linkedCompanyId,

        special_information: data.special_information ?? '',
        links: data.links?.filter((link) => link.url).map((link) => ({
          type: link.type as LinkType,
          url: link.url as string,
        })),
        custom_fields: customFields.getData(),
      }).unwrap();

      enqueueSnackbar(t('contacts.api.update.success'), { variant: 'success' });

    } catch (e) {
      enqueueSnackbar(t('contacts.api.update.default_error'), { variant: 'error' });
    }
  });

  const onLinkChange = useCallback((index: number, value: string) => {
    const updatedLinks = [...links];
    updatedLinks[index] = { ...updatedLinks[index], url: value };

    setValue('links', updatedLinks, { shouldValidate: true, shouldDirty: true });

  }, [links, setValue]);

  return (
    <Stack spacing={2}>
      <Card>
        <CardContent>

          {
            isLoadingContact ? (
              <ListSkeleton />
            ) : (
              <FormProvider methods={methods} onSubmit={handleUpdateContact}>
                <Grid container spacing={2} justifyContent="center" pt={1}>
                  <Grid xs={12}>
                    <RHFSelect
                      name="title"
                      label={t('common.form_labels.title')}
                      fullWidth
                    >
                      <MenuItem value="">{t('common.none')}</MenuItem>
                      {TITLE_OPTIONS.map((value) => (
                        <MenuItem key={value} value={value}>
                          {t(`enums.title_options.${value}`)}
                        </MenuItem>
                      ))}
                    </RHFSelect>
                  </Grid>
                  
                  <Grid xs={6}>
                    <RHFTextField
                      name="first_name"
                      label={t('common.form_labels.first_name')}
                      required
                      fullWidth
                    />
                  </Grid>

                  <Grid xs={6}>
                    <RHFTextField
                      name="last_name"
                      label={t('common.form_labels.last_name')}
                      required
                      fullWidth
                    />
                  </Grid>

                  <Grid xs={12}>
                    <RHFTextField
                      name="email"
                      label={t('common.form_labels.email')}
                      fullWidth
                      disabled={contact?.source !== ContactSource.MANUAL_LINK}
                    />
                  </Grid>

                  <Grid xs={12}>
                    <RHFTextField name="phone" label={t('common.form_labels.phone')} fullWidth />
                  </Grid>

                  <Grid xs={12}>
                    {/* Google location autocomplete for the contact's city/region (JT1-1540). */}
                    <RHFMapsAutocomplete
                      single
                      name="location"
                      options={[]}
                      label={t('common.form_labels.location')}
                      placeholder={t('common.placeholders.location')}
                    />
                  </Grid>

                  <Grid xs={12}>
                    <Stack direction="column" spacing={1}>
                      <RHFTextField
                        name="company_name"
                        label={t('common.form_labels.company_name')}
                        fullWidth
                        value={linkedCompanyName}
                        disabled
                        InputProps={{
                          readOnly: true,
                        }}
                      />

                    </Stack>
                  </Grid>

                  <Grid xs={12}>
                    <RHFTextField
                      name="position_title"
                      label={t('common.form_labels.position_title')}
                      fullWidth
                    />
                  </Grid>

                  <Grid xs={12}>
                    <ReportsToField
                      name="reports_to_contact_id"
                      currentContactId={contactId}
                      options={linkedCompanyId ? (companyContacts ?? []) : undefined}
                      excludeIds={managerExcludeIds}
                    />
                  </Grid>

                  <Grid xs={12}>
                    <RHFTextField
                      name="special_information"
                      label={t('contacts.add_modal.special_information')}
                      multiline
                      rows={4}
                      fullWidth
                    />
                  </Grid>

                  {LinkTypeConfig.map((typeMeta, index) => (
                    <Grid xs={12} key={index}>
                      <Controller
                        name={`links.${index}.url`}
                        control={control}
                        render={({ field, fieldState: { error } }) => (
                          <TextField
                            {...field}
                            fullWidth
                            label={typeMeta.label}
                            placeholder={`Enter your ${typeMeta.type} url`}
                            type="url"
                            onChange={(event) => onLinkChange(index, event.target.value)}
                            error={!!error}
                            helperText={error?.message}
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <Iconify icon={typeMeta.icon} sx={{ color: 'text.disabled' }} />
                                </InputAdornment>
                              ),
                            }}
                          />

                        )}
                      />
                    </Grid>
                  ))}

                  {/* Custom Fields */}
                  {customFields.hasCustomFields && (
                    <>
                      {customFields.renderCustomFields}
                    </>
                  )}

                  <Stack sx={{ py: 2, width: '100%' }} flexDirection="row" justifyContent="flex-end">
                    <LoadingButton
                      loading={isSubmitting}
                      type="submit"
                      variant="contained"
                      disabled={!isValid}
                    >
                      {t('contacts.update')}
                    </LoadingButton>
                  </Stack>
                </Grid>
              </FormProvider>
            )
          }

        </CardContent>
      </Card>

    </Stack>
  )
};